import { Component, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { NavigatorService } from '@app/core/navigator.service';
import { INotification } from '@app/models/INotification';
import { NavigationSectionType } from '@app/models/navigation/navigation-section-type';
import { AnalyticsService } from '@app/app-initialisers/analytics-service/analytics.service';
import { BasketService } from '@app/api/basket.service';
import { Observable } from 'rxjs';
import { BasketItemCreate } from '@app/models/basket/BasketItemCreate';
import { OrderOccasion } from '@app/models/order-occasion';
import { MenuItemView } from '@app/models/menu/menu-item-view';
import { MenuHelperService } from '@app/shared/services/menu-helper/menu-helper.service';
import { MenuService } from '@app/api/menu.service';
import { Deal } from '@app/models/menu/deal';
import { MatDialogRef } from '@angular/material/dialog';
import { MenuDealHelperService } from '@app/shared/services/menu-deal-helper/menu-deal-helper.service';
import { MenuVariantHelperService } from '@app/shared/services/menu-variant-helper/menu-variant-helper.service';
import { OccasionPrice } from '@app/models/menu/occasion-price';
import { ProductUpsellOption } from '@app/modules/menu/components/deal-upsell-modal/deal-upsell-modal.component';

@Component({
  selector: 'app-menu-card',
  styleUrls: ['./menu-card.component.scss'],
  templateUrl: './menu-card.component.html'
})
export class MenuCardComponent extends AndroWebCoreComponent implements OnChanges {
  @ViewChild('futureOrdersDisallowed') private _disallowedModal: TemplateRef<any>;
  @ViewChild('upsellModal') private _upsellModal: TemplateRef<any>;

  @Input() public menuItem: MenuItemView;

  @Input('occasion') private _occasion: OrderOccasion;
  @Input('siteId') private _siteId: string;
  @Input('isStoreOpen') private _isStoreOpen: boolean;
  @Input('customerGroupName') private _customerGroupName: string;
  @Input('notification') private _notification?: Observable<INotification>;
  @Input('wantedTime') public _wantedTime: string;

  public showImages: boolean;
  public activeNotification: INotification;
  public routePath: string;
  public upsellPrompt: string;
  public upsellOptions: { name: string; deal: Deal; }[];
  public upsellDialogRef: MatDialogRef<unknown>;

  constructor(
    private _router: Router,
    private _menuService: MenuService,
    private _basketService: BasketService,
    private _analyticsService: AnalyticsService,
    private _navigatorService: NavigatorService,
    private _menuHelperService: MenuHelperService,
    private _menuDealHelperService: MenuDealHelperService,
    private _menuVariantHelperService: MenuVariantHelperService
  ) {
    super();
    this.showImages = !this.tenant.SuppressProductImages;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes._notification) {
      this.trackNotification();
    }

    this.routePath = this.getRoutePath();
  }

  /**
   * adds the product to basket if the store is open. Opens the disallowed modal if not.
   * @param $event
   */
  public goToItemPage(): void {
    if (!this.menuItem.available || this.menuItem.outOfStock) {
      return;
    }

    if (!this._isStoreOpen) {
      this.openDisallowedModal();
      return;
    }

    if (this.menuItem.type === 'deal') {
      this.trackClickMenuItem();
      this._router.navigateByUrl(this.routePath);
      return;
    }

    if (this.menuItem.upselling) {
      this.openUpsellModal();
      return;
    }

    this.goToProductPage();
  }

  /**
   * navigates to the product page.
   */
  public goToProductPage(): void {
    this.trackClickMenuItem();
    this._router.navigateByUrl(this.routePath);
  }

  /**
   * navigates to the deal page.
   * @param path
   */
  public goToDealPage(deal: Deal): void {
    this.trackClickMenuItem();
    this._router.navigateByUrl(this.getDealRoutePath(deal.Id));
  }

  /**
   * adds the product to basket if the item is simple and addStraightToBasket is true or opens the product page.
   * @param addStraightToBasket
   */
  public async addItemToBasket($event?: MouseEvent): Promise<void> {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    if (this.menuItem.type === 'deal' || !this._menuVariantHelperService.isProductSimple(this.menuItem.variants!)) {
      this.goToItemPage();
      return;
    }

    if (this.menuItem.upselling) {
      this.openUpsellModal();
      return;
    }

    const payload: BasketItemCreate = {
      Modifiers: [],
      Product: {
        Item: this.menuItem.variants[0].Id,
        Quantity: 1
      }
    };

    const price = this.menuItem.variants[0].Prices.find((x: OccasionPrice) => x.Occasion === this._occasion)?.Amount;
    await this._basketService.addProductToBasket(payload, this.menuItem.name, this._customerGroupName, price);
  }

  /**
   * opens the disallowed modal.
   */
  private openDisallowedModal(): void {
    this.openDialog(this._disallowedModal, 'future-orders-disallowed-modal', {
      autoFocus: false,
      disableClose: false,
      width: this.isMobile ? '100%' : ''
    });
  }

  /**
   * subscribes to {@link notification} and updates the {@link activeNotification} property.
   */
  private trackNotification(): void {
    if (this.subscriptions$['MenuDisplayGroupCard-ngOnChanges-notification']) {
      return;
    }

    this.subscriptions$['MenuDisplayGroupCard-ngOnChanges-notification']
      = this._notification?.subscribe((x: INotification) => {
        this.activeNotification = x;

        if (!this.activeNotification) {
          this.subscriptions$['MenuDisplayGroupCard-ngOnChanges-notification'].unsubscribe();
          this.subscriptions$['MenuDisplayGroupCard-ngOnChanges-notification'] = null;
        }
      });
  }

  /**
   * returns the url of the product.
   */
  private getRoutePath(): string {
    const routePath: string = this.menuItem.type === 'deal'
      ? this.getDealRoutePath(this.menuItem.id)
      : this.getProductRoutePath(this.menuItem.id);
    return routePath;
  }

  /**
   * returns the url of the deal.
   * @param dealId
   */
  private getDealRoutePath(dealId: string): string {
    const location: string = this._navigatorService.getLocationBySiteId(this._siteId);

    return this._navigatorService.generateRoutePath(
        location,
        this._occasion,
        NavigationSectionType.Menu,
        this._customerGroupName,
        this.menuItem.name,
        null,
        dealId
    );
  }

  /**
   * returns the url of the product.
   * @param productId
   */
  private getProductRoutePath(productId: string): string {
    const location: string = this._navigatorService.getLocationBySiteId(this._siteId);

    return this._navigatorService.generateRoutePath(
        location,
        this._occasion,
        NavigationSectionType.Menu,
        this._customerGroupName,
        this._navigatorService.clean(this.menuItem.name?.split(' ').join('-')),
        productId
    );
  }

  /**
   * opens the upsell modal.
   */
  private openUpsellModal(): void {
    this.upsellOptions = this.menuItem.upselling.Deals.Ids
        .map((id: string) => this.mapToProductUpsellOption(id, this.menuItem.price))
        .filter((x: ProductUpsellOption) => x);

    if (this.upsellOptions.length === 0) {
      this.goToProductPage();
      return;
    }

    this.upsellPrompt = this.menuItem.upselling.Deals.Prompt ?? 'Would you like to make it a meal?';

    this.upsellDialogRef = this.openDialog(this._upsellModal, 'upsell-modal', {
      autoFocus: false,
      disableClose: false,
      width: this.isMobile ? '100%' : '',
      maxHeight: '100%'
    });
  }

  /**
   * maps the deal id to a product upsell option.
   * @param dealId
   * @param originalPrice the original price of the product.
   */
  private mapToProductUpsellOption(dealId: string, originalPrice: number): ProductUpsellOption {
    const deal: Deal = this._menuService.menuValue.Deals.find((x) => x.Id === dealId);

    if (!deal || deal.OutOfStock || deal.RequiresUnlock) {
      return null;
    }

    const wantedTime = this._wantedTime ? new Date(this._wantedTime) : null;
    const isAvailable = this._menuHelperService.availableForOccasion(deal.Availability?.Values, OrderOccasion[this._occasion], wantedTime);

    if (!isAvailable) {
      return null;
    }

    const price = this._menuDealHelperService.getMinPriceForDeal(deal, this._occasion);
    const currency = '£';
    const total = Math.round((price - originalPrice + Number.EPSILON) * 100) / 100;

    return {
      name: total > 0 ? `${deal.Name} +${currency}${total.toFixed(2)}` : deal.Name,
      deal: deal
    };
  }

  private trackClickMenuItem(): void {
    this._analyticsService.trackClickProduct(
        this.menuItem.name,
        this.menuItem.id,
        this._customerGroupName,
        this.menuItem.price,
        'GBP'
    );
  }
}
